import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CommunicationService } from '../../services/communication.service';
import { publicMethods } from '../../globals';
import {
  isTaxProfilePlay, taxExternalUrl, taxProfileArticles, taxProfileFacts, taxProfilePeople, taxProfilePeriodLabel,
  TAX_PROFILE_PLAYS, TaxProfileArticle, TaxProfileFact, TaxProfilePerson, TaxProfilePlay,
} from './tax-profile-presenters';

interface TaxLandAddress {
  title_number?: string;
  tenure?: string;
  classification?: string;
  price_paid?: number;
  date_proprietor_added?: string;
  address?: Record<string, string | null>;
  epc_data?: Array<{
    report_type?: string;
    property_type?: string;
    asset_rating?: number;
    asset_rating_band?: string;
    floor_area?: number;
    lodgement_date?: string;
  }>;
}

@Component({
  selector: 'tax-accountancy-company-profile',
  templateUrl: './tax-accountancy-company-profile.component.html',
  styleUrls: ['./tax-accountancy-company-profile.component.css'],
  standalone: false,
})
export class TaxAccountancyCompanyProfileComponent implements OnInit, OnDestroy {
  readonly plays = TAX_PROFILE_PLAYS;
  readonly periodLabel = taxProfilePeriodLabel;
  private readonly destroyed$ = new Subject<void>();
  companyNumber = '';
  company: Record<string, any> = {};
  selectedPlay: TaxProfilePlay = 'rd';
  factsByPlay: Record<TaxProfilePlay, TaxProfileFact[]> = {
    rd: [], capital_allowances: [], accounts_audit: [], energy: [], international_tax: [],
  };
  articles: TaxProfileArticle[] = [];
  people: TaxProfilePerson[] = [];
  showAllPeople = false;
  associatedWebsitesOpen = false;
  loading = true;
  loadingError = false;
  hasMagicInsightsAccess = false;
  hasPatentAccess = false;
  hasEnergyAccess = false;
  hasPAFAccess = false;
  landOwnershipData: TaxLandAddress[] = [];
  landOwnershipLoading = false;
  private landRequestedFor = '';
  private landRequestToken = 0;
  savingDefault = false;
  defaultSaved = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService,
    private communicationService: CommunicationService,
    private titleService: Title,
    private pubMethods: publicMethods,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      const play = params.get('play');
      this.selectedPlay = isTaxProfilePlay(play) ? play : 'rd';
    });
    this.communicationService.getHasAIRAAccess().pipe(takeUntil(this.destroyed$))
      .subscribe(access => this.hasMagicInsightsAccess = !!access);
    this.communicationService.getHasPatentsAccessMessage().pipe(takeUntil(this.destroyed$))
      .subscribe(access => {
        this.hasPatentAccess = !!access;
        this.rebuildFacts();
      });
    this.communicationService.getHasEnergyAndEmissionsAccessMessage().pipe(takeUntil(this.destroyed$))
      .subscribe(access => {
        this.hasEnergyAccess = !!access;
        this.rebuildFacts();
      });
    this.communicationService.getHasPAFAccess().pipe(takeUntil(this.destroyed$))
      .subscribe(access => {
        this.hasPAFAccess = !!access;
        if (!this.hasPAFAccess) {
          this.landRequestToken++;
          this.landRequestedFor = '';
          this.landOwnershipData = [];
          this.landOwnershipLoading = false;
        } else {
          this.loadLandOwnershipIfPermitted();
        }
      });
    this.route.paramMap.pipe(
      map(params => params.get('id') || ''),
      distinctUntilChanged(),
      tap(number => {
        this.companyNumber = number;
        this.company = {};
        this.articles = [];
        this.people = [];
        this.landRequestToken++;
        this.landRequestedFor = '';
        this.landOwnershipData = [];
        this.landOwnershipLoading = false;
        this.showAllPeople = false;
        this.associatedWebsitesOpen = false;
        this.factsByPlay = { rd: [], capital_allowances: [], accounts_audit: [], energy: [], international_tax: [] };
        this.loading = true;
        this.loadingError = false;
      }),
      switchMap(number => number
        ? this.companyService.getCompanyProfileData(number).pipe(catchError(() => of(null)))
        : of(null)),
      takeUntil(this.destroyed$),
    ).subscribe(data => {
      this.company = data?.company || {};
      this.loadingError = !this.company.company_overview;
      this.loading = false;
      if (this.loadingError) return;
      this.articles = taxProfileArticles(this.company);
      this.people = taxProfilePeople(this.company);
      this.rebuildFacts();
      this.loadLandOwnershipIfPermitted();
      this.titleService.setTitle((this.company.company_overview.name || 'Company') + ' | Zint Tax & Accountancy');
    });
  }

  private loadLandOwnershipIfPermitted(): void {
    if (!this.hasPAFAccess || !this.companyNumber || !this.company?.company_overview ||
      this.landRequestedFor === this.companyNumber) return;
    const number = this.companyNumber;
    const token = ++this.landRequestToken;
    this.landRequestedFor = number;
    this.landOwnershipLoading = true;
    this.companyService.getLandRegistryAddresses(number).pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: addresses => {
          if (token !== this.landRequestToken || !this.hasPAFAccess || this.companyNumber !== number) return;
          this.landOwnershipData = Array.isArray(addresses) ? addresses.map(address => ({
            ...address,
            epc_data: Array.isArray(address?.epc_data) ? address.epc_data : [],
          })) : [];
          this.landOwnershipLoading = false;
        },
        error: () => {
          if (token !== this.landRequestToken || !this.hasPAFAccess || this.companyNumber !== number) return;
          this.landOwnershipData = [];
          this.landOwnershipLoading = false;
        },
      });
  }

  landAddressText(address: TaxLandAddress): string {
    return Object.values(address.address || {}).filter(Boolean).join(', ') || 'Address not recorded';
  }

  private rebuildFacts(): void {
    if (!this.company?.company_overview) return;
    const visible = {
      ...this.company,
      patents_filed: this.hasPatentAccess ? this.company.patents_filed : [],
      energy_and_emissions: this.hasEnergyAccess ? this.company.energy_and_emissions : {},
    };
    for (const play of this.plays) this.factsByPlay[play.id] = taxProfileFacts(visible, play.id);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  selectPlay(play: TaxProfilePlay): void {
    this.selectedPlay = play;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { play },
      queryParamsHandling: 'merge',
    });
  }

  get selectedPlayLabel(): string {
    return this.plays.find(play => play.id === this.selectedPlay)?.label || 'Tax and accountancy';
  }

  get selectedPlayDescription(): string {
    return this.plays.find(play => play.id === this.selectedPlay)?.description || '';
  }

  get visibleFacts(): TaxProfileFact[] {
    return this.factsByPlay[this.selectedPlay];
  }

  get latestAccountsPeriod(): string {
    return this.company?.financials?.made_up_to_dates?.[0] || '';
  }

  get latestAccountsSubmitted(): string {
    const accounts = this.company?.financials?.accounts || {};
    const section: any = Object.values(accounts).find((item: any) => item?.columns?.length);
    return section?.columns?.[0]?.submitted || '';
  }

  get accountant(): string {
    return this.company?.financials?.financial_providers?.accountant || '';
  }

  get auditor(): string {
    return this.company?.financials?.financial_providers?.auditor || '';
  }

  get websiteUrl(): string | undefined {
    return this.safeHttpUrl(this.company?.company_overview?.url);
  }

  get websiteDomain(): string {
    const url = this.websiteUrl;
    return url ? new URL(url).hostname.replace(/^www\./i, '') : '';
  }

  get associatedWebsites(): { url: string; label: string }[] {
    const sites = this.company?.company_overview?.associated_websites;
    if (!Array.isArray(sites)) return [];
    return sites.map((site: any) => {
      const url = this.safeHttpUrl(site?.url);
      return url ? { url, label: String(site?.domain_name || new URL(url).hostname) } : null;
    }).filter((site): site is { url: string; label: string } => !!site);
  }

  get phoneNumber(): string {
    const overview = this.company?.company_overview;
    const primary = String(overview?.phone_number || '').trim();
    if (this.phoneHrefFor(primary)) return primary;
    const alternatives = Array.isArray(overview?.phone_numbers) ? overview.phone_numbers : [];
    const fallback = alternatives.find((item: any) => !item?.fax && this.phoneHrefFor(String(item?.number || '')));
    return fallback ? String(fallback.number).trim() : '';
  }

  get phoneHref(): string {
    return this.phoneHrefFor(this.phoneNumber);
  }

  get emailAddress(): string {
    const email = String(this.company?.company_overview?.email || '').trim();
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email) ? email : '';
  }

  get emailHref(): string {
    return this.emailAddress ? `mailto:${this.emailAddress}` : '';
  }

  get linkedInUrl(): string | undefined {
    return this.safeHttpUrl(this.company?.company_overview?.linkedin_url, ['linkedin.com']);
  }

  get xUrl(): string | undefined {
    return this.safeHttpUrl(this.company?.company_overview?.twitter_url, ['twitter.com', 'x.com']);
  }

  get facebookUrl(): string | undefined {
    return this.safeHttpUrl(this.company?.company_overview?.facebook_url, ['facebook.com', 'fb.com']);
  }

  private safeHttpUrl(value: unknown, domains?: string[]): string | undefined {
    const candidate = taxExternalUrl(value);
    if (!candidate) return undefined;
    try {
      const url = new URL(candidate);
      if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || url.username || url.password) return undefined;
      if (domains && !domains.some(domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`))) return undefined;
      return url.toString();
    } catch { return undefined; }
  }

  private phoneHrefFor(value: string): string {
    const phone = value.trim();
    if (!/^\+?[\d\s().-]{6,}$/.test(phone) || phone.replace(/\D/g, '').length < 6) return '';
    const dialable = phone.startsWith('+') ? phone.replace(/\(0\)/, '') : phone;
    const digits = dialable.replace(/\D/g, '');
    return `tel:${phone.startsWith('+') ? '+' : ''}${digits}`;
  }

  get hasRecordedEnergy(): boolean {
    if (!this.hasEnergyAccess) return false;
    return (this.company?.energy_and_emissions?.energy || []).some((item: any) =>
      item.total_energy_kwh != null || item.electric_kwh != null || item.gas_kwh != null);
  }

  get visibleHiring(): Record<string, any>[] {
    return (this.company?.hiring_for || []).slice(0, 3);
  }

  get visibleGrants(): Record<string, any>[] {
    return (this.company?.grants || []).slice(0, 3);
  }

  get visiblePatents(): Record<string, any>[] {
    return this.hasPatentAccess ? (this.company?.patents_filed || []).slice(0, 3) : [];
  }

  get hasActivity(): boolean {
    return !!(this.articles.length || this.visibleHiring.length || this.visibleGrants.length || this.visiblePatents.length);
  }

  externalUrl(value: unknown): string | undefined {
    return taxExternalUrl(value);
  }

  makeDefaultView(): void {
    if (this.savingDefault) return;
    this.savingDefault = true;
    this.authenticationService.setDefaultSkin('tax_accountancy').subscribe({
      next: () => {
        this.savingDefault = false;
        this.defaultSaved = true;
        this.pubMethods.showInfoMessage('Tax & accountancy is now your default view.');
      },
      error: () => {
        this.savingDefault = false;
        this.pubMethods.showInfoMessage('Could not save your default view. Please try again.');
      },
    });
  }

  openFullProfile(): void {
    this.authenticationService.setDefaultSkin('full').subscribe({
      next: () => this.router.navigate(['/profile', this.companyNumber], {
        queryParams: this.route.snapshot.queryParams,
        fragment: this.route.snapshot.fragment,
      }),
      error: () => this.pubMethods.showInfoMessage('Could not open the full profile. Please try again.'),
    });
  }
}
